import { AfterViewInit, Component, OnDestroy, OnInit, signal, ViewContainerRef, WritableSignal } from '@angular/core';
import { LMBaseComponent } from '@base/lm-base.component';
import {
  LMBusyIndicatorService,
  LMComponentInfoService,
  LMContextService,
  LMErrorUtilService, LMFormatUtilService,
  LMLoggerService,
  LMModalOpenService,
  LMModalWrapperService,
  LMRouteComponentService,
  LMUrlInfoBuilderService,
  LMUrlUtilService
} from '@app/services';
import {
  AppConstants,
  LMContextUtilService,
  LMDialogButtonKey,
  LMGlobalUtilService,
  LMUniqueIdService
} from '@app/utils';
import { LMAppComponentService } from '@utils/lm-app-component.service';
import {
  LMBannerHeaderComponentRef,
  LMBuilderContext,
  LMBuilderEvent,
  LMBuilderService,
  LMFieldPickerService
} from '@app/components';
import { Router } from '@angular/router';
import { LMBuilderModel } from "@components/builder/services/lm-builder-model.service";
import { LMBuilderToolbarService } from "@components/builder/services/lm-builder-toolbar.service";
import { forkJoin, Subject, Subscription } from "rxjs";
import { LMComponentTrackerService } from "@services/lm-component-tracker.service";
import { takeUntil } from "rxjs/operators";
import { ListResource } from "@app/resources";
import { Location } from '@angular/common';
import { LMFieldPickerSelectOptions, LMFieldPickerSelectResponse } from "@modal/field-picker/lm-field-picker";

@Component({
  selector:    'lm-builder',
  standalone: false,
  providers:   [ LMBuilderModel, LMBuilderToolbarService, LMComponentTrackerService ],
  templateUrl: './lm-builder.component.html',
  styleUrl: './lm-builder.component.scss',
})
export class LMBuilderComponent extends LMBaseComponent implements AfterViewInit, OnInit, OnDestroy {
  lpl: WritableSignal<string> = signal('');
  listSpec: WritableSignal<LMListSpec | undefined> = signal(undefined);
  listUrlInfo: WritableSignal<LMListUrlInfo | undefined> = signal(undefined);
  userFolderJson: WritableSignal<string> = signal('');

  hasError = false;
  errorHeader = Soho.Locale.translate('UnexpectedErrorOccurred');
  errorMessage = '';
  spec: WritableSignal<LMListPSpec | undefined> = signal(undefined);

  private readonly instanceId: string;
  pageTitle = "Report Builder";     // TODO translate
  listTitleOverride: string | undefined;
  private screenId = 'ReportBuilder';
  private subscriptionsArray: Array<Subscription> = [];
  private readonly destroyer$ = new Subject<void>();
  private builderContext: LMBuilderContext = 'UNKNOWN';
  facade: WritableSignal<LMBusinessClassFacade | undefined> = signal(undefined);
  canAdd = signal(true);
  dragTooltip = signal('');
  addTooltip = signal('');
  dropTargets = [ LMBuilderModel.DISPLAY_FIELDS_LIST, LMBuilderModel.COLUMN_TOTAL_LIST, LMBuilderModel.SUMMARY_TOTAL_LIST, LMBuilderModel.INSTANCE_SELECTION, LMBuilderModel.LIST_PREVIEW_EMPTY_STATE ];

  constructor(
      protected router: Router,
      protected location: Location,
      protected lmContextService: LMContextService,
      protected lmContextUtilService: LMContextUtilService,
      protected lmLoggerService: LMLoggerService,
      protected lmComponentTrackerService: LMComponentTrackerService,
      protected lmBusyIndicatorService: LMBusyIndicatorService,
      protected lmComponentInfoService: LMComponentInfoService,
      protected lmUniqueIdService: LMUniqueIdService,
      protected lmAppComponentService: LMAppComponentService,
      protected lmModalWrapperService: LMModalWrapperService,
      protected lmFormatUtilService: LMFormatUtilService,
      protected lmModalOpenService: LMModalOpenService,
      protected lmBannerHeaderCompRef: LMBannerHeaderComponentRef,
      protected lmBuilderToolbarService: LMBuilderToolbarService,
      protected lmUrlUtilService: LMUrlUtilService,
      protected lmGlobalUtilService: LMGlobalUtilService,
      protected lmErrorUtilService: LMErrorUtilService,
      protected lmBuilderService: LMBuilderService,
      protected lmBuilderModel: LMBuilderModel,
      protected lmUrlInfoBuilderService: LMUrlInfoBuilderService,
      protected currentComponentService: LMRouteComponentService,
      protected lmFieldPickerService: LMFieldPickerService
  ) {
    super(lmBusyIndicatorService, lmComponentInfoService, lmUniqueIdService, lmAppComponentService, lmModalWrapperService, lmModalOpenService);
    this.instanceId = this.lmLoggerService.instanceId;
    this.lmBannerHeaderCompRef.instance.toolbarSectionTitle = 'Report Creation';
    this.lmBusyIndicatorService.hideBusyIndicator();
  }

  ngOnInit() {
    this.currentComponentService.setRouteComponent(this as Component);
  }

  ngAfterViewInit(): void {
    this.lmBusyIndicatorService.showBusyIndicator();

    forkJoin({
      facade:  this.lmBuilderModel.getFacade().pipe(
      ),
      spec: this.lmBuilderModel.getSpec().pipe(
      ),
    }).subscribe({
      next: (response)    => {
          this.build(response.spec, response.facade);
        },
      error: (error)  => { this.handleError(error);  },
    });

    this.lmBuilderModel.builderEvent$.pipe(
        takeUntil(this.destroyer$))
        .subscribe(event => {
          this.handleBuilderEvent(event);
     });

    this.lmBuilderModel.builderContext$.subscribe(builderContext => {
      this.handleBuilderContextChange(builderContext);
    });

    this.buildToolBar();
  }

  ngOnDestroy(): void {
    if (this.lmBannerHeaderCompRef && this.lmBannerHeaderCompRef.instance) {
      this.lmBannerHeaderCompRef.instance.toolbarSectionTitle = '';
    }
    this.subscriptionsArray.forEach(subscription => {
      subscription.unsubscribe();
    });
    this.lmBuilderToolbarService.reset();
    if (this.lmContextService.isTrackerEnabled()) {
      this.lmComponentTrackerService.endComponentTracking();
    }
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  getComponentPlaceHolder(): ViewContainerRef | undefined {
    return undefined;
  }

  getSpec(): LMListSpec | LMFormSpec | LMPageSpec | undefined {
    return undefined;
  }

  getUserWebRequestId(): string {
    return 'builder';  //TODO Create a id for this page
  }

  isRouteReuseActive(): boolean {
    return false;
  }

  setRouteReuseActive(active: boolean, keys?: string): void {
    // not supported
  }

  saveComponentState(includeSubComponents?: boolean) {
    // nothing to save
  }

  private handleFacadeResponse(facade: LMBusinessClassFacade | undefined) {
    if (this.lmBuilderService.responseHasError(facade)) {
      this.handleError(facade);
      return;
    }

    if (facade) {
      this.facade.set(facade);
    }
  }

  private handleBuilderResponse(spec: LMListPSpec | undefined) {
    if (this.lmBuilderService.responseHasError(spec)) {
      this.handleError(spec);
      return;
    }

    if (spec) {
      this.spec.set(spec);
    }
  }

  private build(spec: LMListPSpec | undefined, facade: LMBusinessClassFacade | undefined) {
    this.hideBusyIndicator();
    if (spec && facade) {
      this.updateListTitle(spec, true);
      this.updatePageTitle(spec, facade, true);
      this.updatePreview();
      this.handleFacadeResponse(facade);
      this.handleBuilderResponse(spec); // do this last, it triggers the UI to build
    }
  }

  private handleBuilderEvent(event: LMBuilderEvent) {
    if (this.spec()) {
      switch (event) {
        case LMBuilderEvent.DISPLAY_FIELD_ADDED:
        case LMBuilderEvent.DISPLAY_FIELD_REMOVED:
        case LMBuilderEvent.DISPLAY_FIELD_REORDERED:
        case LMBuilderEvent.COLUMN_TOTALS_CHANGED:
        case LMBuilderEvent.SUMMARY_TOTALS_CHANGED:
          this.updatePreview();
          break;
      }
    }
  }

  private handleError(error: any) {
    this.lmBusyIndicatorService.hideBusyIndicator();
    this.hasError = true;
    this.errorMessage = error.message;
    this.lmErrorUtilService.handleUnexpectedError(error);
  }

  private updatePageTitle(spec: LMListPSpec | undefined, facade: LMBusinessClassFacade | undefined, updateUserFolderName:boolean) {
    if (spec && facade) {
      if (this.lmBuilderModel.mode === 'createReport') {
        this.pageTitle = this.buildCreateReportPageTitle(spec, facade, updateUserFolderName);
      } else {
        this.pageTitle = this.buildPersonalizePageTitle(spec);
      }

      this.lmBannerHeaderCompRef.instance.toolbarSectionTitle = this.pageTitle;
    }
  }

  private updateListTitle(spec: LMListPSpec | undefined, updateUserFolderName:boolean) {
    if (spec) {
      if (this.lmBuilderModel.mode === 'createReport') {
        this.updateCreateReportListTitle(spec, updateUserFolderName);
      } else {
        this.updatePersonalizationListTitle(spec);
      }
    }
  }

  private buildCreateReportPageTitle(spec: LMListPSpec, facade: LMBusinessClassFacade, initialBuild:boolean): string {
    if (initialBuild) {
      const listResource = this.lmBuilderModel.listResource;
      const copyingReport = this.lmBuilderModel.userFolderResource ? true : false;
      const copyReportName = copyingReport ? spec.userFolder?.name : undefined;
      const copyingList = listResource.listName !== undefined ? true : false;
      const copyListName = copyingList ? spec.titleIs.display : undefined;

      if (copyReportName) {
        return this.lmFormatUtilService.formatMessage(Soho.Locale.translate('CopyReportFrom'), copyReportName);
      }
      if (copyListName) {
        return this.lmFormatUtilService.formatMessage(Soho.Locale.translate('CopyReportFrom'), copyListName);
      }

      const busClassName = facade.name ? facade.name : spec.businessClass;
      return this.lmFormatUtilService.formatMessage(Soho.Locale.translate('AddReportFor'), busClassName);
    } else {
      return this.pageTitle;
    }
  }

  private buildPersonalizePageTitle(spec: LMListPSpec): string {
    if (spec.userFolder) {
      return this.lmFormatUtilService.formatMessage(Soho.Locale.translate('EditSettingsFor'), spec.userFolder.name);
    } else {
      return this.lmFormatUtilService.formatMessage(Soho.Locale.translate('PersonalizeFor'), spec.titleIs.display);
    }
  }

  private updatePersonalizationListTitle(spec: LMListPSpec) {
    if (spec.userFolder) {
      this.listTitleOverride = spec.userFolder.name;
    } else {
      this.listTitleOverride = spec.titleIs.display;
    }
  }

  private updateCreateReportListTitle(spec: LMListPSpec, updateUserFolderName:boolean) {
    if (spec.userFolder) {
      if (updateUserFolderName) {
          spec.userFolder.name = this.lmBuilderModel.buildDefaultReportName();
      }

      this.listTitleOverride = spec.userFolder.name;
    }
  }

  private buildToolBar() {
    this.lmBannerHeaderCompRef.instance.toolbarButtons = this.lmBuilderToolbarService.getToolbarButtonsObservable();

    setTimeout(() => {
      // need to place in a timeout so the toolbarService has a change to set its observable member.
      this.lmBannerHeaderCompRef.instance.screenId = this.screenId;
      this.lmBannerHeaderCompRef.instance.screenAutomationId = this.screenId;
      this.lmBannerHeaderCompRef.instance.toolbarSectionTitle = this.pageTitle;
      this.lmBuilderToolbarService.buildToolbar(this.lmBuilderModel.mode);
      if (!this.lmContextService.isBannerInRoute() && this.lmContextService.showBreadcrumbs()) {
        this.lmBannerHeaderCompRef.resetBreadCrumbs('ReportBuilder');
        this.lmBannerHeaderCompRef.updateBreadcrumbs({
          id: undefined,
          category: 'ReportBuilder',
          busClass: '',
          specName: 'ReportBuilder',
          type: 'SYSTEM',
          title: this.pageTitle,
          routeUrl: this.router.url
        });
      }
      this.subscriptionsArray.push(this.lmBannerHeaderCompRef.instance.toolbarButtonEvent.subscribe((e) => {
        if (e.actionReference?.id === 'lm-builder-save' || e.actionReference?.id === 'lm-builder-save-view' ) {
          this.save(e.actionReference?.id === 'lm-builder-save-view');
        } else if (e.actionReference?.id === 'lm-builder-update-preview') {
            this.updateListTitle(this.spec(), false);
            this.updatePageTitle(this.spec(), this.facade(), false);
          this.updatePreview();
        } else if (e.actionReference?.id === 'lm-builder-close') {
          this.location.back();
        }
      }));
    });
  }

  private pageAnalytics() {
    const loadTime = this.lmLoggerService.timeEnd('>> ReportBuilder' + this.instanceId, [ this, 'ReportBuilder', 'end' ]);
    const componentInfo = this.getComponentInfo();
    this.lmComponentTrackerService.endLoadTracking('ReportBuilder', this.router.url, componentInfo, loadTime);
    this.lmComponentTrackerService.startComponentTracking(componentInfo, loadTime);
  }

  getComponentInfo(): LMComponentInfo {
    const formInfo: LMComponentInfo = {
      componentType: 'REPORT_BUILDER',
      name: 'ReportBuilder',
      userWebRequestId: this.getUserWebRequestId(),
      type: 'page',
      developer: false,
      isWizard: false,
      panelNavigation: false,
      hasConfigAccess: false,
    };
    return formInfo;
  }

  private save(viewReport: boolean) {
    this.lmBuilderModel.save().subscribe({
      next: (v)       => { this.handleSaveResponse(v, viewReport);  },
      error: (e)                            => { this.handleError(e);                     },
      complete: ()                                => {                                          }
    });
  }

  private handleSaveResponse(response: LMBuilderCreateReportResponse, viewReport: boolean) {
      if (response.status === 'COMPLETED') {
        // if (this.lmBuilderModel.mode === 'createReport' && response.reportUrl && viewReport) {
        if (viewReport && response.reportUrl) {
            this.lmGlobalUtilService.navigateByUrl(response.reportUrl, { replaceUrl: true });
        } else {
          this.lmGlobalUtilService.displayToastMessage('Save', 'Changes successfully changed', AppConstants.TRA_ACTION_COMPLETED);
          if (this.lmBuilderModel.mode === 'createReport' && this.spec()) {
            // @ts-ignore
            const resourceId = `${this.spec().businessClass}.${this.spec().listName}`;
            const listResource = new ListResource({ resourceId: resourceId });
            const url = this.lmBuilderService.getPersonalizeSpecUrl(listResource, response.userfolder);
            this.lmGlobalUtilService.navigateByUrl(url, { replaceUrl: true });
          }
        }
      } else {
        this.lmErrorUtilService.handleUnexpectedError(response);
      }
  }

  private updatePreview() {
    this.listSpec.set(undefined); // needed to switch from list view to data grid

    this.lmBuilderModel.updatePreview().subscribe({
      next: (v)       => { this.handleUpdatePreviewResponse(v);     },
      error: (e)      => { this.handleError(e);                     },
      complete: ()    => {                                          }
    });
  }

  private handleUpdatePreviewResponse(response: LMBuilderUpdatePreviewResponse) {
      if (response.status === 'COMPLETED') {
        const modelLpl = response.modelLpl ? response.modelLpl : '';
        this.lpl.set(modelLpl);

        if (response.listSpec) {
          this.listSpec.set(response.listSpec);
          this.listUrlInfo.set(this.lmUrlInfoBuilderService.getListUrlInfoFromListSpec(response.listSpec));
        } else {
          this.listSpec.set(undefined);
        }

        this.lmBuilderModel.getSpec().subscribe(spec => {
          if (spec) {
            this.userFolderJson.set(JSON.stringify(spec.userFolder, null, 2));
          }
        });
      } else {
        this.lmErrorUtilService.handleUnexpectedError(response);
      }
  }

  handleAdvancedSearch(): void {
    let fieldPickerUrl = this.lmBuilderModel.getBuilderUrl();
    fieldPickerUrl = this.lmUrlUtilService.addParamToUrl(fieldPickerUrl, 'requesttype', 'fieldSpecForBusClass');

    const options: LMFieldPickerSelectOptions = {
      view: 'Fields',
      businessClass: this.lmBuilderModel.listResource.busClassName,
      mode: 'advanced'
    };

    this.lmFieldPickerService.openFieldPickerDialog(options, fieldPickerUrl, undefined as unknown as ViewContainerRef)
        .subscribe((result: LMFieldPickerSelectResponse) => {
          this.addFieldPickerField(result);
        });
  }

  addFieldPickerField(result: LMFieldPickerSelectResponse) {
    if ('DISPLAY_FIELDS' === this.builderContext) {
      if (this.lmBuilderModel.allowColumnFieldForUiFieldSpec(result.uiFieldSpec)) {
        this.lmBuilderModel.addDisplayFieldForUiFieldSpec(-1, result);
      } else {
        this.showAddConstraint();
      }
    } else if ('COLUMN_TOTAL_FIELDS' === this.builderContext) {
      if (this.lmBuilderModel.allowColumnTotalForUiFieldSpec(result.uiFieldSpec)) {
        this.lmBuilderModel.addColumnTotalDisplayFieldForUiFieldSpec(-1, result);
      } else {
        this.showAddConstraint();
      }
    } else if ('SUMMARY_TOTAL_FIELDS' === this.builderContext) {
      if (this.lmBuilderModel.allowSummaryTotalForUiFieldSpec(result.uiFieldSpec)) {
        this.lmBuilderModel.addSummaryTotalFieldForUiFieldSpec(result);
      } else {
        this.showAddConstraint();
      }
    } else if ('INSTANCE_SELECTION' === this.builderContext) {
      this.addCustomFilter(result.fieldName);
    } else {
      this.showAddConstraint();
    }
  }

  private addCustomFilter(fieldId: string) {
    this.lmBuilderModel.addCustomFilter(fieldId);
  }

  private showAddConstraint() {
    const dialogId = 'lm-builder-field-constraint';
    const buttons = [
      {
        id: `${dialogId}-${LMDialogButtonKey.CLOSE}`,
        text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
        click: (e: any, modal: { close: (arg0: boolean) => void; }) => {
          modal.close(true);
        }
      },
    ];
    let title = 'Field Not Added';
    let message = 'Builder Not Context does not accept a field';
    switch(this.builderContext) {
      case "DISPLAY_FIELDS":
        title = Soho.Locale.translate('ColumnNotAdded');
        message = Soho.Locale.translate('ColumnNotAddedMsg');
        break;
      case "COLUMN_TOTAL_FIELDS":
        title = Soho.Locale.translate('ColumnNotAdded');
        message =  Soho.Locale.translate('ColumnTotalNotAddedMsg');
        break;
      case "SUMMARY_TOTAL_FIELDS":
        title = Soho.Locale.translate('SummaryNotAdded');
        message =  Soho.Locale.translate('SummaryNotAddedMsg');
        break;
      default:
    }
    const attributes: SohoAttributesArray = [ { name:  'data-automation-id',  value: 'lm-builder-field-constraint-modal' } ];
    this.lmModalWrapperService
        .message()
        .attributes(attributes)
        .title(title)
        .message(message)
        .buttons(buttons)
        .open();
  }

  private handleBuilderContextChange(builderContext: LMBuilderContext) {
    this.builderContext = builderContext;
    this.canAdd.set(builderContext === 'DISPLAY_FIELDS' || builderContext === 'COLUMN_TOTAL_FIELDS' || builderContext === 'SUMMARY_TOTAL_FIELDS' || builderContext === 'INSTANCE_SELECTION');

    switch(this.builderContext) {
      case 'DISPLAY_FIELDS':
        this.addTooltip.set(Soho.Locale.translate('AddFieldToColumns'));
        this.dragTooltip.set(Soho.Locale.translate('DragFieldToColumns'));
        break;
      case 'COLUMN_TOTAL_FIELDS':
        this.addTooltip.set(Soho.Locale.translate('AddFieldToColumns'));
        this.dragTooltip.set(Soho.Locale.translate('DragFieldToColumns'));
        break;
      case 'SUMMARY_TOTAL_FIELDS':
        this.addTooltip.set(Soho.Locale.translate('AddFieldToSummaries'));
        this.dragTooltip.set(Soho.Locale.translate('DragFieldToSummaries'));
        break;
      case 'INSTANCE_SELECTION':
        this.addTooltip.set(Soho.Locale.translate('AddFilter'));
        this.dragTooltip.set(Soho.Locale.translate('DragToAddFilter'));
        break;
      default:
        this.addTooltip.set('');
        this.dragTooltip.set('');
    }
  }

  handleAddField(builderField: LMBuilderField) {
    if ('DISPLAY_FIELDS' === this.builderContext) {
      if (this.lmBuilderModel.allowColumnField(builderField)) {
        this.lmBuilderModel.addDisplayField(-1, builderField);
      } else {
        this.showAddConstraint();
      }
    } else if ('COLUMN_TOTAL_FIELDS' === this.builderContext) {
      if (this.lmBuilderModel.allowColumnTotal(builderField)) {
        this.lmBuilderModel.addColumnTotalDisplayField(-1, builderField);
      } else {
        this.showAddConstraint();
      }
    } else if ('SUMMARY_TOTAL_FIELDS' === this.builderContext) {
      if (this.lmBuilderModel.allowSummaryTotal(builderField)) {
        this.lmBuilderModel.addSummaryTotalField(builderField);
      } else {
        this.showAddConstraint();
      }
    }  else if ('INSTANCE_SELECTION' === this.builderContext) {
      this.onAddCustomFilterBuilderField(builderField);
    } else {
      this.showAddConstraint();
    }
  }

  private onAddCustomFilterBuilderField(builderField: LMBuilderField) {
    const fieldId = this.lmBuilderModel.buildFieldId(builderField);
    this.addCustomFilter(fieldId);
  }
}
